import React, { useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { useLanguage } from '../context/LanguageContext';
import { Star, MapPin, Phone, Mail, Globe, ExternalLink } from 'lucide-react';
import VerifiedBadge from './VerifiedBadge';
import { resolveContact } from '../lib/contact';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom paw marker with improved design
const createPawIcon = (isHighlighted = false, hasLogo = false) => {
  const size = isHighlighted ? 44 : 36;
  const innerSize = isHighlighted ? 24 : 18;
  
  return L.divIcon({
    className: 'custom-paw-marker',
    html: `
      <div class="paw-marker-wrapper ${isHighlighted ? 'highlighted' : ''}" style="
        position: relative;
        width: ${size}px;
        height: ${size}px;
        transform: translateY(-50%);
      ">
        <div style="
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: ${isHighlighted ? 'linear-gradient(135deg, #1f2937 0%, #374151 100%)' : 'linear-gradient(135deg, #FFD500 0%, #FFC000 100%)'};
          border: 3px solid ${isHighlighted ? '#FFD500' : '#ffffff'};
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          box-shadow: ${isHighlighted 
            ? '0 4px 20px rgba(255, 213, 0, 0.4), 0 2px 8px rgba(0,0,0,0.2)' 
            : '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)'};
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        "></div>
        <div style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: ${innerSize}px;
          height: ${innerSize}px;
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <svg xmlns="http://www.w3.org/2000/svg" width="${innerSize}" height="${innerSize}" viewBox="0 0 24 24" fill="${isHighlighted ? '#FFD500' : '#1f2937'}" stroke="none">
            <ellipse cx="8" cy="6" rx="2.5" ry="3"/>
            <ellipse cx="16" cy="6" rx="2.5" ry="3"/>
            <ellipse cx="5" cy="12" rx="2" ry="2.5"/>
            <ellipse cx="19" cy="12" rx="2" ry="2.5"/>
            <path d="M12 22c-4 0-7-3-7-7 0-2 1-4 3-5 1-1 2.5-1.5 4-1.5s3 0.5 4 1.5c2 1 3 3 3 5 0 4-3 7-7 7z"/>
          </svg>
        </div>
        ${isHighlighted ? `
          <div style="
            position: absolute;
            top: -4px;
            left: -4px;
            right: -4px;
            bottom: -4px;
            border: 2px solid #FFD500;
            border-radius: 50% 50% 50% 0;
            transform: rotate(-45deg);
            animation: pulse-ring 1.5s ease-out infinite;
            opacity: 0;
          "></div>
        ` : ''}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size + 8],
  });
};

// Cluster icon with custom styling
const createClusterCustomIcon = (cluster) => {
  const count = cluster.getChildCount();
  let size = 40;
  let fontSize = 14;
  
  if (count >= 50) {
    size = 55;
    fontSize = 16;
  } else if (count >= 20) {
    size = 48;
    fontSize = 15;
  }
  
  return L.divIcon({
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background: linear-gradient(135deg, #FFD500 0%, #FFC000 100%);
        border: 3px solid #ffffff;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 15px rgba(255, 213, 0, 0.4), 0 2px 6px rgba(0,0,0,0.1);
        font-weight: 700;
        font-size: ${fontSize}px;
        color: #1f2937;
        font-family: 'Inter', sans-serif;
      ">
        ${count}
      </div>
    `,
    className: 'custom-cluster-icon',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

// Map center handler with smooth animation
const MapCenterHandler = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.flyTo(center, zoom || 14, { 
        duration: 0.8,
        easeLinearity: 0.25
      });
    }
  }, [center, zoom, map]);
  
  return null;
};

// Map bounds handler to fit all markers
const MapBoundsHandler = ({ businesses }) => {
  const map = useMap();
  
  useEffect(() => {
    if (businesses.length > 0) {
      const validBusinesses = businesses.filter(b => b.latitude && b.longitude);
      if (validBusinesses.length > 0) {
        const bounds = L.latLngBounds(
          validBusinesses.map(b => [b.latitude, b.longitude])
        );
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
      }
    }
  }, [businesses, map]);
  
  return null;
};

// A11y: Leaflet renders raster tiles as <img> elements without an alt
// attribute, which the Aug 2026 SEO audit flagged (12 imagens sem alt).
// The tiles are purely decorative, so we inject alt="" and role="presentation"
// once the map has mounted and after every layer change.
const LeafletTileA11yFix = () => {
  const map = useMap();
  useEffect(() => {
    const container = map?.getContainer?.();
    if (!container) return undefined;
    const fix = () => {
      container.querySelectorAll('img.leaflet-tile').forEach((img) => {
        if (!img.hasAttribute('alt')) {
          img.setAttribute('alt', '');
          img.setAttribute('role', 'presentation');
        }
      });
    };
    fix();
    const observer = new MutationObserver(fix);
    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [map]);
  return null;
};


const MapView = ({ businesses, highlightedBusiness, onMarkerClick, fitBounds = false }) => {
  const { t, translateCategory, language } = useLanguage();
  const mapRef = useRef(null);
  
  // Zurich center
  const defaultCenter = [47.3769, 8.5417];
  
  const validBusinesses = useMemo(() => 
    businesses.filter(b => b.latitude && b.longitude),
    [businesses]
  );
  
  const mapCenter = highlightedBusiness?.latitude 
    ? [highlightedBusiness.latitude, highlightedBusiness.longitude]
    : null;

  const renderStars = (rating) => {
    if (!rating) return (
      <span className="text-xs text-gray-400 italic">
        {language === 'de' ? 'Keine Bewertung' : 'No rating'}
      </span>
    );
    return (
      <div className="flex items-center gap-1">
        <div className="flex items-center">
          {[...Array(5)].map((_, i) => (
            <Star 
              key={i} 
              className={`h-3.5 w-3.5 ${i < Math.floor(rating) ? 'fill-[#FFD500] text-[#FFD500]' : 'fill-gray-200 text-gray-200'}`}
            />
          ))}
        </div>
        <span className="text-sm font-semibold text-gray-700">{rating.toFixed(1)}</span>
      </div>
    );
  };

  return (
    <div className="h-full w-full relative" data-testid="map-container">
      {/* Custom CSS for animations */}
      <style>{`
        @keyframes pulse-ring {
          0% { transform: rotate(-45deg) scale(1); opacity: 0.6; }
          100% { transform: rotate(-45deg) scale(1.3); opacity: 0; }
        }
        .paw-marker-wrapper.highlighted {
          z-index: 1000 !important;
        }
        .leaflet-popup-content-wrapper {
          border-radius: 12px !important;
          box-shadow: 0 10px 40px rgba(0,0,0,0.15) !important;
          padding: 0 !important;
          overflow: hidden;
        }
        .leaflet-popup-content {
          margin: 0 !important;
          width: auto !important;
        }
        .leaflet-popup-tip {
          box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
        }
        .leaflet-container {
          font-family: 'Inter', sans-serif !important;
        }
        .custom-cluster-icon {
          background: transparent !important;
        }
        .leaflet-control-zoom {
          border: none !important;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1) !important;
          border-radius: 8px !important;
          overflow: hidden;
        }
        .leaflet-control-zoom a {
          width: 36px !important;
          height: 36px !important;
          line-height: 36px !important;
          font-size: 18px !important;
          color: #374151 !important;
          border-bottom: 1px solid #e5e7eb !important;
        }
        .leaflet-control-zoom a:last-child {
          border-bottom: none !important;
        }
        .leaflet-control-zoom a:hover {
          background: #f9fafb !important;
          color: #111827 !important;
        }
      `}</style>
      
      <MapContainer
        ref={mapRef}
        center={defaultCenter}
        zoom={11}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        zoomControl={false}
      >
        {/* Map tiles - OpenStreetMap standard (free, no API key required) */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={19}
        />
        
        {/* Zoom controls on the right */}
        <ZoomControl position="topright" />
        
        {/* Handle map center changes */}
        {mapCenter && <MapCenterHandler center={mapCenter} zoom={15} />}        
        {/* Fit bounds on initial load if requested */}
        {fitBounds && <MapBoundsHandler businesses={validBusinesses} />}
        {/* Inject alt="" on all Leaflet tile images for accessibility */}
        <LeafletTileA11yFix />
        
        {/* Marker cluster group for better performance */}
        <MarkerClusterGroup
          chunkedLoading
          iconCreateFunction={createClusterCustomIcon}
          maxClusterRadius={60}
          spiderfyOnMaxZoom={true}
          showCoverageOnHover={false}
          animate={true}
          animateAddingMarkers={true}
        >
          {validBusinesses.map((business) => (
            <Marker
              key={business.id}
              position={[business.latitude, business.longitude]}
              icon={createPawIcon(highlightedBusiness?.id === business.id, !!business.logo_url)}
              eventHandlers={{
                click: () => onMarkerClick && onMarkerClick(business),
              }}
            >
              <Popup>
                <div className="w-[280px]" data-testid={`popup-${business.id}`}>
                  {/* Header with logo or category */}
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      {business.logo_url ? (
                        <img 
                          src={business.logo_url} 
                          alt={business.name}
                          className="w-10 h-10 rounded-lg object-cover border border-gray-200"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-[#FFD500] flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#1f2937" stroke="none">
                            <ellipse cx="8" cy="6" rx="2.5" ry="3"/>
                            <ellipse cx="16" cy="6" rx="2.5" ry="3"/>
                            <ellipse cx="5" cy="12" rx="2" ry="2.5"/>
                            <ellipse cx="19" cy="12" rx="2" ry="2.5"/>
                            <path d="M12 22c-4 0-7-3-7-7 0-2 1-4 3-5 1-1 2.5-1.5 4-1.5s3 0.5 4 1.5c2 1 3 3 3 5 0 4-3 7-7 7z"/>
                          </svg>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 text-sm leading-tight truncate">
                          {business.name}
                        </h3>
                        <div className="mt-1"><VerifiedBadge business={business} size="sm" /></div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white text-gray-600 mt-1 border border-gray-200">
                          {translateCategory(business.category)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="px-4 py-3 space-y-2.5">
                    {/* Rating */}
                    <div>{renderStars(business.rating)}</div>
                    
                    {/* Address */}
                    <div className="flex items-start gap-2 text-sm text-gray-600">
                      <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5 text-gray-400" />
                      <span className="leading-tight">{business.address}</span>
                    </div>
                    
                    {/* Action buttons */}
                    <div className="flex gap-2 pt-1">
                      {(() => {
                        const c = resolveContact(business);
                        if (c.type === 'none') return null;
                        const Icon = c.type === 'phone' ? Phone : Mail;
                        return (
                          <a
                            href={c.href}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg transition-colors"
                          >
                            <Icon className="h-3.5 w-3.5" />
                            {language === 'en' ? c.label.en : c.label.de}
                          </a>
                        );
                      })()}
                      {business.website && (
                        <a 
                          href={business.website.startsWith('http') ? business.website : `https://${business.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg transition-colors"
                        >
                          <Globe className="h-3.5 w-3.5" />
                          Website
                        </a>
                      )}
                      <a 
                        href={`/anbieter/${business.slug || business.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-medium text-white bg-[#FFD500] hover:bg-[#E5C000] px-3 py-2 rounded-lg transition-colors"
                        style={{ color: '#1f2937' }}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        {language === 'de' ? 'Details' : 'Details'}
                      </a>
                    </div>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MarkerClusterGroup>
      </MapContainer>
      
      {/* Business count badge */}
      <div className="absolute bottom-4 left-4 z-[1000] bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-lg border border-gray-200">
        <span className="text-sm font-medium text-gray-700">
          {validBusinesses.length} {language === 'de' ? 'Anbieter' : 'providers'}
        </span>
      </div>
    </div>
  );
};

export default MapView;
